/**
 * Feature E02-F05 — Kick-off questionnaire React view (T03 + T04 + T07).
 *
 * 1:1 port of `../EER app/public/assets/js/project-kickoff.js` renderStage3Content.
 * One component covers the three coupled tasks:
 *
 *   - T03 — the questionnaire: questions grouped by CATEGORY (icon per category)
 *     with a per-category answered/total counter and a "+" add button; each
 *     question has clickable (inline-editable) text, a delete button, and an
 *     answer <textarea> that AUTO-RESIZES and supports the bullet-point helper
 *     (Enter continues a "- " bullet, an empty bullet is removed, Ctrl+Shift+L
 *     toggles a bullet). The "Market salary rate" question renders the salary
 *     search button (SalaryRateButton).
 *   - T04 — Questions ↔ Notes mode toggle (Mode A: questions centre / notes aside;
 *     Mode B: notes centre / questionnaire cheat-sheet aside); notes + transcript
 *     auto-save (debounced 800ms, Saving/Saved/error indicator); a "Complete
 *     Meeting" button that saves with meeting_completed=true (after which a
 *     different button set shows).
 *   - T07 — missing-data detection: a yellow block listing unanswered questions
 *     (first 5, expandable to the full list), with an AI Pre-fill action + a
 *     "Search Company Website" action shown only while the meeting is not completed
 *     AND there are unanswered questions. PK-QA-16: the website action is now a
 *     pure client-side Google `site:`-lookup (opens a new tab), gated on the
 *     human-entered `Client.website` — no AI call.
 *
 * Thin shell — every rule lives in `kickoffModel.ts`; persistence in `kickoffApi.ts`
 * (`useSaveKickoff` sends the merged updates, with meeting_completed for Complete
 * Meeting). The salary button is `SalaryRateButton` (E03-F06).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { isCompanyWebsiteFactsFailure, type KickoffQuestionItem } from '@eer/shared';
import { useToast } from '../ui/ToastContext.js';
import { ApiResultError } from '../api/client.js';
import { SalaryRateButton } from './SalaryRateButton.js';
import {
  useSaveKickoff,
  useFlushKickoffSave,
  usePrefillQuestionnaire,
  useKickoffEnrichment,
  useCompanyWebsiteFacts,
} from './kickoffApi.js';
import {
  addQuestion,
  answeredCounts,
  applyBulletKey,
  buildKickoffSaveUpdates,
  categoryIcon,
  detectMissing,
  groupByCategory,
  isSalaryQuestion,
  rehydrateQuestions,
  removeQuestion,
  setAnswer,
  setQuestionText,
  toggleViewMode,
  type KickoffViewMode,
} from './kickoffModel.js';
import { readKickoffEnrichment, readTranscriptSummary } from './kickoffEnrichmentModel.js';
import {
  companyFactsFailureMessage,
  companyFactsTransportMessage,
  readCompanyFacts,
  COMPANY_FACTS_FALLBACK_MESSAGE,
} from './companyFactsModel.js';
import { CompanyFactsPanel } from './CompanyFactsPanel.js';
import { KickoffDataPanel } from './KickoffDataPanel.js';
import { useProjectDetail } from '../project/projectDetailApi.js';

/** ms to debounce the notes/transcript auto-save (legacy 800). */
export const AUTOSAVE_DEBOUNCE_MS = 800;

/** The autosave status indicator state. */
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface KickoffViewProps {
  /** Project id (the PATCH /projects/:id save target). */
  readonly projectId: number;
  /** The merged questionnaire (template + position-type + saved answers). */
  readonly initialQuestions: readonly KickoffQuestionItem[];
  /** Saved kick-off notes (projects.data.kickoff_notes). */
  readonly initialNotes?: string;
  /** Saved kick-off transcript (projects.data.kickoff_transcript). */
  readonly initialTranscript?: string;
  /** Whether the meeting is already completed (drives the button set). */
  readonly meetingCompleted?: boolean;
  /** Project role_name (the salary search primary roleName source). */
  readonly projectRoleName?: string | null;
}

export function KickoffView({
  projectId,
  initialQuestions,
  initialNotes = '',
  initialTranscript = '',
  meetingCompleted = false,
  projectRoleName,
}: KickoffViewProps): JSX.Element {
  const toast = useToast();
  const saveMutation = useSaveKickoff(projectId);
  const flushSave = useFlushKickoffSave(projectId);
  const prefillMutation = usePrefillQuestionnaire(projectId);
  const enrichmentMutation = useKickoffEnrichment(projectId);
  // E03-F02-T11 — the real «Search Company Website» (scrape + Haiku, no web_search).
  const companyFactsMutation = useCompanyWebsiteFacts(projectId);

  // The AI enrichment + transcript summary live on projects.data — not props here.
  // Read them from the SHARED per-project detail cache (qk.projects.detail), already
  // populated by the host (ProjectDetailView), so this is a cache hit, not a request.
  // After AI Processing invalidates that key the panel re-hydrates automatically.
  const detailQuery = useProjectDetail(projectId);
  const projectData = detailQuery.data?.project.data ?? null;
  const kickoffEnrichment = readKickoffEnrichment(projectData);
  const transcriptSummary = readTranscriptSummary(projectData);
  // E03-F02-T11 — what «Search Company Website» collected, from the SAME bag
  // (`projects.data.company_facts`). The button's mutation invalidates
  // qk.projects.detail, so a successful run re-hydrates this read and the panel
  // appears without any extra query of its own.
  const companyFacts = readCompanyFacts(projectData);

  // PK-QA-16 — the human-entered company website (`Client.website`, set on the
  // Pre-Kickoff screen). It gates the "Search Company Website" button below: with
  // no known site there is nothing to download, so the button stays disabled and
  // points at the screen where the site is entered (the api would answer
  // `no_company_website` anyway — this just saves the round trip). Read from the
  // same per-project detail cache (cache hit).
  const clientWebsite = detailQuery.data?.project.client_website?.trim() || null;

  const [questions, setQuestions] = useState<KickoffQuestionItem[]>(() => [...initialQuestions]);
  const [notes, setNotes] = useState(initialNotes);
  const [transcript, setTranscript] = useState(initialTranscript);
  const [mode, setMode] = useState<KickoffViewMode>('questions');
  const [completed, setCompleted] = useState(meetingCompleted);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [showAllMissing, setShowAllMissing] = useState(false);

  const groups = useMemo(() => groupByCategory(questions), [questions]);
  const missing = useMemo(() => detectMissing(questions), [questions]);
  const totals = useMemo(() => answeredCounts(questions), [questions]);

  // ---- auto-save (debounced) ---------------------------------------------
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // E12-F08-T15 — the local questionnaire as of the last SENT save (or the last
  // hydration). Rows that differ from it are local edits the server has not seen.
  const sentBaseRef = useRef<KickoffQuestionItem[]>([...initialQuestions]);

  // E02-F05-T09 — notes/transcript are sent ONLY once the user has edited them in
  // this visit. An untouched field is left out of the save bag, so an autosave
  // triggered by a question edit / AI pre-save can never overwrite the saved text
  // with '' (the reported data loss).
  const notesTouchedRef = useRef(false);
  const transcriptTouchedRef = useRef(false);
  // A debounced save is scheduled but not yet sent (flushed on unmount/pagehide).
  const pendingRef = useRef(false);

  // Mirror the live state into refs so the debounced save (which fires after the
  // edit closure was created) always reads the CURRENT notes/transcript/questions
  // — never a stale closure snapshot (the classic debounce + state pitfall).
  const liveRef = useRef({ notes, transcript, questions });
  liveRef.current = { notes, transcript, questions };

  // After every save (and after an AI action) the per-project query is invalidated
  // and the parent passes a NEW `initialQuestions`. E12-F08-T15: re-hydrate only the
  // rows WITHOUT local edits since the last sent save — an answer typed while the
  // save was in flight must not be replaced by the server copy (it goes out with
  // the next autosave). Untouched rows take the server copy, so AI Pre-fill / AI
  // Processing results still appear (legacy `refreshProject` + re-render).
  // Reads the live list via the ref (not a state updater) so the baseline update
  // stays a single side effect even under StrictMode double-invocation.
  useEffect(() => {
    const merged = rehydrateQuestions(
      liveRef.current.questions,
      sentBaseRef.current,
      initialQuestions,
    );
    sentBaseRef.current = merged.base;
    setQuestions(merged.questions);
  }, [initialQuestions]);

  /** The save bag from the live state (untouched notes/transcript omitted). */
  const buildUpdates = useCallback((meetingCompleted?: boolean) => {
    const live = liveRef.current;
    // E12-F08-T15 — this is what the server will hold once the save lands.
    sentBaseRef.current = [...live.questions];
    return buildKickoffSaveUpdates({
      notes: notesTouchedRef.current ? live.notes : undefined,
      transcript: transcriptTouchedRef.current ? live.transcript : undefined,
      questions: live.questions,
      meetingCompleted,
    });
  }, []);

  /** Cancel the scheduled debounced save (an explicit save supersedes it). */
  const cancelScheduled = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    pendingRef.current = false;
  }, []);

  const persist = useCallback(
    (extra?: { meetingCompleted?: boolean }) => {
      cancelScheduled();
      const updates = buildUpdates(extra?.meetingCompleted);
      setSaveStatus('saving');
      saveMutation.mutate(updates, {
        onSuccess: () => setSaveStatus('saved'),
        onError: () => setSaveStatus('error'),
      });
    },
    [saveMutation, buildUpdates, cancelScheduled],
  );

  /** Schedule a debounced auto-save (notes/transcript edits, legacy 800ms). */
  const scheduleAutoSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingRef.current = true;
    setSaveStatus('saving');
    debounceRef.current = setTimeout(() => persist(), AUTOSAVE_DEBOUNCE_MS);
  }, [persist]);

  // E02-F05-T09 — an edit made right before leaving must not be lost: flush the
  // pending debounced save instead of dropping it. Two exits are covered:
  //   * in-app (step switch / route change) → the component unmounts;
  //   * reload / tab close → `pagehide` (keepalive fetch survives the unload).
  const flushRef = useRef<(keepalive: boolean) => void>(() => {});
  flushRef.current = (keepalive: boolean) => {
    if (!pendingRef.current) return;
    const updates = buildUpdates();
    cancelScheduled();
    flushSave(updates, { keepalive });
  };

  useEffect(() => {
    const onPageHide = (): void => flushRef.current(true);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
      flushRef.current(false);
    };
  }, []);

  // ---- T04: mode toggle (preserve notes/transcript before re-render) -------
  function toggleMode(): void {
    // Values are already in state (controlled inputs), so they survive the
    // re-render; flip the mode (legacy saved them explicitly before swapping).
    setMode((m) => toggleViewMode(m));
  }

  // ---- T04: complete meeting ---------------------------------------------
  function completeMeeting(): void {
    // Legacy `completeMeeting` ~756: warn before completing an empty meeting.
    if (!notes.trim() && !transcript.trim()) {
      if (
        !window.confirm(
          'Notes and transcript are empty. Are you sure you want to complete the meeting?',
        )
      ) {
        return;
      }
    }
    setCompleted(true);
    cancelScheduled();
    setSaveStatus('saving');
    const updates = buildUpdates(true);
    saveMutation.mutate(updates, {
      onSuccess: () => {
        setSaveStatus('saved');
        toast.success('Meeting completed');
      },
      onError: () => {
        setSaveStatus('error');
        setCompleted(false);
        toast.error('Save error');
      },
    });
  }

  // ---- Manual save (legacy `saveKickoffData` ~718 — the explicit Save button) --
  function saveKickoffData(): void {
    cancelScheduled();
    setSaveStatus('saving');
    const updates = buildUpdates();
    saveMutation.mutate(updates, {
      onSuccess: () => {
        setSaveStatus('saved');
        toast.success('Kick-off data saved');
      },
      onError: () => {
        setSaveStatus('error');
        toast.error('Save error');
      },
    });
  }

  // ---- AI actions --------------------------------------------------------

  /**
   * Persist the current notes/transcript/questions before an AI action so the
   * server reads the just-typed data (legacy prefill ~627 / runKickoffAi ~798
   * pre-save). Resolves once the save mutation settles.
   */
  function persistBeforeAi(): Promise<void> {
    cancelScheduled();
    setSaveStatus('saving');
    const updates = buildUpdates();
    return new Promise<void>((resolve) => {
      saveMutation.mutate(updates, {
        onSuccess: () => {
          setSaveStatus('saved');
          resolve();
        },
        // A pre-save failure should NOT abort the AI action (parity: legacy
        // swallowed the pre-save error with `.catch(() => {})`).
        onError: () => {
          setSaveStatus('error');
          resolve();
        },
      });
    });
  }

  /**
   * AI Pre-fill Questionnaire (`prefillQuestionnaire` ~604). Save first, then POST
   * prefill; the server persists the merged answers and the invalidate refetch
   * re-hydrates the list (via the initialQuestions effect above).
   */
  async function onPrefill(): Promise<void> {
    if (prefillMutation.isPending) return;
    await persistBeforeAi();
    prefillMutation.mutate(undefined, {
      onSuccess: (res) => {
        if (res.success === false) {
          toast.error(res.error || 'Pre-fill error');
          return;
        }
        toast.success('Questionnaire pre-filled');
      },
      onError: () => toast.error('Error during AI pre-fill'),
    });
  }

  /**
   * Process Transcript (`processTranscript` ~581): require a non-empty transcript,
   * then reuse the prefill flow (its v2 pass extracts answers from the transcript).
   */
  async function onProcessTranscript(): Promise<void> {
    if (prefillMutation.isPending) return;
    if (!transcript.trim()) {
      toast.error('Paste transcript text first');
      return;
    }
    await onPrefill();
  }

  /**
   * E03-F02-T11 — "Search Company Website" now really searches the company website.
   *
   * History: the legacy AI route billed an Anthropic `web_search` per click and was
   * ~always a no-op, so PK-QA-16 (decision-log 2026-07-21) replaced it with a plain
   * Google tab — cheap, but it filled nothing and showed nothing in the app. T11
   * swaps that stub for the pipeline the Research chain already uses: the api
   * downloads the client's own site (free, cache-first, SSRF-guarded) and extracts
   * the company profile with Haiku — no `web_search`, one explicit click, no
   * background retry (no-auto-AI policy). The facts land on
   * `projects.data.company_facts` and appear in the Company Facts panel.
   *
   * A project with no known site costs nothing: the api answers `no_company_website`
   * before touching a provider, and we show that as a toast pointing at Pre-Kickoff.
   */
  function onSearchWebsite(): void {
    if (!clientWebsite || companyFactsMutation.isPending) return;
    companyFactsMutation.mutate(undefined, {
      onSuccess: (res) => {
        // The negative outcome ("no website known", "site unreachable") is a
        // SUCCESSFUL 200 `{ collected:false, code, message }` and lands here.
        // Branch on the guard, never on `success === false`: the web client
        // strips a top-level `success:true` and demotes a `success:false` 200
        // body to a transport error (`isLegacyErrorPayload`), so a `success`
        // test in this callback is dead code by construction.
        if (isCompanyWebsiteFactsFailure(res)) {
          toast.error(companyFactsFailureMessage(res));
          return;
        }
        const domain = res.facts.domain || 'the company website';
        toast.success(`Company facts collected from ${domain}`);
      },
      // Real failures only (auth, network, 5xx, a body that fails the schema) —
      // plus one legacy case: an OLD api still answering `{ success:false, error }`
      // at HTTP 200, which the client demotes to an error. Only an `ApiResultError`
      // carries a message meant for a human; anything else (e.g. a thrown ZodError
      // on schema drift) must NOT be toasted raw.
      onError: (e) =>
        toast.error(
          e instanceof ApiResultError
            ? companyFactsTransportMessage(e.detail.message)
            : COMPANY_FACTS_FALLBACK_MESSAGE,
        ),
    });
  }

  /**
   * AI Processing (`runKickoffAi` ~787): post-completion enrichment. Save first,
   * then POST kickoff_enrichment over the saved data.
   */
  async function onRunKickoffAi(): Promise<void> {
    if (enrichmentMutation.isPending) return;
    await persistBeforeAi();
    enrichmentMutation.mutate(undefined, {
      onSuccess: (res) => {
        if (res.success === false) {
          toast.error(res.error || 'AI processing error');
          return;
        }
        // B3/KS-10 — this action now ALSO merges the meeting notes/transcript into
        // the answers, so say so: silence is what made Ksenia think nothing ran.
        toast.success('AI processing completed — questionnaire updated from the meeting');
      },
      onError: () => toast.error('Error during AI Kickoff processing'),
    });
  }

  // ---- T03: question mutations -------------------------------------------
  function onAnswerChange(idx: number, value: string): void {
    setQuestions((qs) => setAnswer(qs, idx, value));
    scheduleAutoSave();
  }

  function onAnswerKeyDown(idx: number, e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    const el = e.currentTarget;
    const edit = applyBulletKey(
      e.key,
      { shift: e.shiftKey, ctrl: e.ctrlKey },
      el.value,
      el.selectionStart,
    );
    if (edit) {
      e.preventDefault();
      setQuestions((qs) => setAnswer(qs, idx, edit.value));
      // Restore the caret after React re-renders the controlled value.
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = edit.caret;
      });
      scheduleAutoSave();
    }
  }

  function onAddQuestion(category: string): void {
    setQuestions((qs) => {
      const { questions: next, insertedIdx } = addQuestion(qs, category);
      setEditingIdx(insertedIdx);
      return next;
    });
  }

  function onRemoveQuestion(idx: number): void {
    setQuestions((qs) => removeQuestion(qs, idx));
    scheduleAutoSave();
  }

  function onEditText(idx: number, value: string): void {
    setQuestions((qs) => setQuestionText(qs, idx, value));
  }

  function autoResize(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 28)}px`;
  }

  const showMissingActions = !completed && missing.count > 0;
  const missingList = showAllMissing ? missing.unanswered : missing.short;

  // ---- Extracted panels: ONE JSX source, rendered in EITHER slot ----------
  // The questionnaire checklist and the notes/transcript block each render
  // EXACTLY once; the mode only decides which one occupies the wide "main" slot
  // (flex-1) vs the fixed-width "aside" cheat-sheet. Keeping a single source per
  // panel (rather than duplicating the JSX in both slots) is the ADR invariant:
  // Mode B's aside questionnaire is the SAME editable checklist as Mode A's main
  // (identical inline-edit / salary button / auto-resize answers), never a
  // read-only variant. Legacy `renderStage3Content` reuses `checklistHtml`
  // verbatim across both modes; this mirrors that.

  // T03: questionnaire grouped by category (legacy `checklistHtml`).
  const renderQuestionnaire = (): JSX.Element => (
    <section key="kickoff-questionnaire" data-testid="kickoff-questions" className="space-y-4">
      {groups.map((group) => (
        <div
          key={group.category}
          data-testid={`kickoff-category-${group.category}`}
          className="rounded-lg border border-border bg-white p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <h4 className="flex items-center gap-2 font-semibold text-text-strong">
              <i className={`fa-solid ${categoryIcon(group.category)}`} aria-hidden />
              {group.category}
              <span
                data-testid={`kickoff-cat-count-${group.category}`}
                className="text-xs font-normal text-text-muted"
              >
                {group.answered}/{group.total}
              </span>
            </h4>
            <button
              type="button"
              data-testid={`kickoff-add-${group.category}`}
              onClick={() => onAddQuestion(group.category)}
              aria-label={`Add question to ${group.category}`}
              className="rounded px-2 text-text-muted hover:bg-surface-muted hover:text-text-strong"
            >
              <i className="fa-solid fa-plus" aria-hidden />
            </button>
          </div>

          <ul className="space-y-3">
            {group.items.map((q) => (
              <li key={q.idx} data-testid={`kickoff-question-${q.idx}`}>
                <div className="mb-1 flex items-start justify-between gap-2">
                  {editingIdx === q.idx ? (
                    <input
                      type="text"
                      autoFocus
                      data-testid={`kickoff-qtext-input-${q.idx}`}
                      value={q.text}
                      onChange={(e) => onEditText(q.idx, e.target.value)}
                      onBlur={() => setEditingIdx(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') setEditingIdx(null);
                        if (e.key === 'Escape') setEditingIdx(null);
                      }}
                      className="w-full rounded border border-border px-2 py-1 text-sm outline-none focus:border-primary"
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid={`kickoff-qtext-${q.idx}`}
                      onClick={() => setEditingIdx(q.idx)}
                      className="flex-1 text-left text-sm font-medium text-text"
                    >
                      {q.text}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`kickoff-delete-${q.idx}`}
                    onClick={() => onRemoveQuestion(q.idx)}
                    aria-label="Delete question"
                    className="shrink-0 text-text-muted hover:text-danger"
                  >
                    <i className="fa-solid fa-trash" aria-hidden />
                  </button>
                </div>
                <div className="flex items-start gap-1">
                  <textarea
                    data-testid={`kickoff-answer-${q.idx}`}
                    value={q.answer ?? ''}
                    rows={1}
                    ref={(el) => {
                      if (el) autoResize(el);
                    }}
                    onChange={(e) => {
                      autoResize(e.currentTarget);
                      onAnswerChange(q.idx, e.target.value);
                    }}
                    onKeyDown={(e) => onAnswerKeyDown(q.idx, e)}
                    placeholder="Answer…"
                    className="w-full resize-none overflow-hidden rounded border border-border px-2 py-1 text-sm outline-none focus:border-primary"
                  />
                  {isSalaryQuestion(q) ? (
                    <SalaryRateButton
                      projectId={projectId}
                      questionIdx={q.idx}
                      question={q}
                      questions={questions}
                      projectRoleName={projectRoleName}
                      onResult={(idx, rate) => onAnswerChange(idx, rate)}
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );

  // T04: notes / transcript block (legacy `notesBlockHtml`).
  const renderNotes = (): JSX.Element => (
    <section key="kickoff-notes-block" data-testid="kickoff-notes" className="space-y-3">
      <div>
        <label
          htmlFor="kickoff-notes-text"
          className="block text-xs font-semibold uppercase tracking-wide text-text-muted"
        >
          Meeting Notes
        </label>
        <textarea
          id="kickoff-notes-text"
          data-testid="kickoff-notes-text"
          value={notes}
          rows={4}
          onChange={(e) => {
            notesTouchedRef.current = true;
            setNotes(e.target.value);
            scheduleAutoSave();
          }}
          className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div>
        <label
          htmlFor="kickoff-transcript-text"
          className="block text-xs font-semibold uppercase tracking-wide text-text-muted"
        >
          Transcript
        </label>
        <textarea
          id="kickoff-transcript-text"
          data-testid="kickoff-transcript-text"
          value={transcript}
          rows={4}
          onChange={(e) => {
            transcriptTouchedRef.current = true;
            setTranscript(e.target.value);
            scheduleAutoSave();
          }}
          className="mt-1 w-full rounded border border-border px-2 py-1.5 text-sm outline-none focus:border-primary"
        />
        <button
          type="button"
          data-testid="kickoff-process-transcript"
          onClick={() => void onProcessTranscript()}
          disabled={prefillMutation.isPending}
          className="mt-2 rounded border border-border px-3 py-1 text-xs font-medium hover:bg-surface-muted disabled:opacity-60"
        >
          {prefillMutation.isPending ? (
            <>
              <i
                className="fa-solid fa-spinner fa-spin mr-1"
                data-testid="kickoff-process-transcript-spinner"
                aria-hidden
              />
              Processing transcript…
            </>
          ) : (
            'Process Transcript'
          )}
        </button>

        {/* Transcript Summary block (legacy project-kickoff.js ~195-198) —
            shown only when the AI wrote a summary of the transcript. */}
        {transcriptSummary ? (
          <div
            data-testid="kickoff-transcript-summary"
            className="mt-2 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-slate-700"
          >
            <div className="mb-1 font-bold text-blue-600">
              <i className="fa-solid fa-file-lines mr-1" aria-hidden />
              Transcript Summary
            </div>
            <div className="whitespace-pre-wrap">{transcriptSummary}</div>
          </div>
        ) : null}
      </div>
    </section>
  );

  // T07 missing-data warning block (PK-QA-18: split out of the former
  // `renderActions()` so it can render on its own, directly ABOVE the questionnaire
  // in whichever slot the questionnaire currently occupies — the invariant "yellow
  // Missing-data plate is always right above the questionnaire" must hold in BOTH
  // modes, not just when the questionnaire is the wide main column). Returns null
  // when there is nothing missing, so it never renders an empty container.
  const renderMissingBlock = (): JSX.Element | null =>
    missing.count > 0 ? (
      <div
        data-testid="kickoff-missing"
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
      >
        <div className="flex items-center justify-between">
          <span data-testid="kickoff-missing-count" className="font-semibold">
            <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden />
            Missing data: {missing.count} unanswered
          </span>
          {showMissingActions ? (
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="kickoff-ai-prefill"
                onClick={() => void onPrefill()}
                disabled={prefillMutation.isPending}
                className="rounded bg-primary px-2 py-1 text-xs font-semibold text-surface hover:bg-primary-hover disabled:opacity-60"
              >
                {prefillMutation.isPending ? (
                  <>
                    <i
                      className="fa-solid fa-spinner fa-spin mr-1"
                      data-testid="kickoff-ai-prefill-spinner"
                      aria-hidden
                    />
                    AI analyzing…
                  </>
                ) : (
                  'AI Pre-fill Questionnaire'
                )}
              </button>
              {/* E03-F02-T11 — reads the company's OWN website: the api downloads
                  the pages (free, cache-first) and Haiku extracts the profile —
                  never `web_search` (PK-QA-16 killed that route for cost). The
                  result lands in the Company Facts panel in the side column. Gated
                  on the human-entered company website: when empty it is disabled
                  with a tooltip pointing at the Pre-Kickoff screen where the
                  website is set (nothing to download ⇒ nothing to spend). */}
              <button
                type="button"
                data-testid="kickoff-search-website"
                onClick={onSearchWebsite}
                disabled={!clientWebsite || companyFactsMutation.isPending}
                title={
                  clientWebsite
                    ? `Read ${clientWebsite} and extract company facts with AI (no web search) — shown in the Company Facts panel`
                    : 'Add the company website on the Pre-Kickoff screen to enable this lookup'
                }
                className="rounded border border-amber-300 px-2 py-1 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
              >
                {companyFactsMutation.isPending ? (
                  <>
                    <i
                      className="fa-solid fa-spinner fa-spin mr-1"
                      data-testid="kickoff-search-website-spinner"
                      aria-hidden
                    />
                    Reading website…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-globe mr-1" aria-hidden />
                    Search Company Website
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
        {/* The button above disables (native `disabled`) instead of toasting when
            clientWebsite is empty — a hover-only `title` explains why, but a
            hover tooltip is invisible to anyone who does not hover (a real
            client complaint: "clicking does nothing"). Say the same thing
            in a line that is always on screen. */}
        {showMissingActions && !clientWebsite ? (
          <p data-testid="kickoff-search-website-hint" className="mt-1 text-xs text-amber-700">
            <i className="fa-solid fa-circle-info mr-1" aria-hidden />
            Add the company website on the Pre-Kickoff screen to enable &ldquo;Search Company
            Website&rdquo;.
          </p>
        ) : null}
        <ul data-testid="kickoff-missing-list" className="mt-2 list-inside list-disc space-y-0.5">
          {missingList.map((q, i) => (
            <li key={i} data-testid="kickoff-missing-item">
              {q.text}
            </li>
          ))}
        </ul>
        {missing.hasMore ? (
          <button
            type="button"
            data-testid="kickoff-missing-toggle"
            onClick={() => setShowAllMissing((v) => !v)}
            className="mt-1 text-xs font-medium underline"
          >
            {showAllMissing ? 'Show less' : `Show all ${missing.count}`}
          </button>
        ) : null}
      </div>
    ) : null;

  // T04 Save + Complete Meeting / post-completion AI Processing button set
  // (PK-QA-18: split out of the former `renderActions()`).
  //
  // KS2-07 (2026-07-31): «в интерфейсе kickoff не видна кнопка "complete meeting"
  // … надо ее и все кнопки действий вынести наверх в непрокручиваемую зону». The
  // row used to sit at the BOTTOM of the questionnaire column, which is a
  // fixed-height scroller — so the button existed only for whoever scrolled the
  // whole questionnaire to its end. It now renders in the header strip, above the
  // two panes and outside their scroll area.
  const renderSaveRow = (): JSX.Element => (
    <div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* Manual Save — always available (legacy `saveKickoffData` Save button). */}
        <button
          type="button"
          data-testid="kickoff-save"
          onClick={saveKickoffData}
          disabled={saveMutation.isPending}
          className="rounded border border-border px-4 py-2 text-sm font-semibold text-text hover:bg-surface-muted disabled:opacity-60"
        >
          {saveMutation.isPending ? (
            <>
              <i
                className="fa-solid fa-spinner fa-spin mr-1"
                data-testid="kickoff-save-spinner"
                aria-hidden
              />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </button>

        {completed ? (
          <>
            <span
              data-testid="kickoff-completed-badge"
              className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800"
            >
              <i className="fa-solid fa-check mr-1" aria-hidden />
              Meeting completed
            </span>
            {/* Post-completion enrichment (legacy `runKickoffAi` — AI Processing). */}
            <button
              type="button"
              data-testid="kickoff-ai-processing"
              onClick={() => void onRunKickoffAi()}
              disabled={enrichmentMutation.isPending}
              className="rounded bg-accent px-4 py-2 text-sm font-semibold text-primary hover:bg-accent-hover disabled:opacity-60"
            >
              {enrichmentMutation.isPending ? (
                <>
                  <i
                    className="fa-solid fa-spinner fa-spin mr-1"
                    data-testid="kickoff-ai-processing-spinner"
                    aria-hidden
                  />
                  AI processing…
                </>
              ) : (
                'AI Processing'
              )}
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid="kickoff-complete-meeting"
            onClick={completeMeeting}
            disabled={saveMutation.isPending}
            className="rounded bg-accent px-4 py-2 text-sm font-semibold text-primary hover:bg-accent-hover disabled:opacity-60"
          >
            Complete Meeting
          </button>
        )}
      </div>
    </div>
  );

  // "Kickoff Data" AI-enrichment panel — clickable sections (Summary / Company /
  // Position / Requirements / Compensation / Search-Sourcing / Missing data) →
  // detail dialog (legacy renderKickoffPanel). Renders nothing when the project
  // has no kickoff_enrichment yet. It lives at the bottom of the ACTIVE aside in
  // BOTH modes (legacy: aside of Mode A next to notes, aside of Mode B under the
  // questionnaire cheat-sheet) — never in the wide main slot.
  const dataPanel = <KickoffDataPanel enrichment={kickoffEnrichment} />;

  // E03-F02-T11 — "Company Facts": WHAT the «Search Company Website» button
  // brought back (profile + the pages actually read). Sits directly above the
  // Kickoff Data panel in the active aside, so the answer to the click is on the
  // same screen as the click — the whole point of retiring the Google-tab stub,
  // which left the app with nothing to show. Renders null until a run persisted
  // `projects.data.company_facts`.
  const companyFactsPanel = <CompanyFactsPanel facts={companyFacts} />;

  const isNotesMode = mode === 'notes';

  return (
    <div data-testid="kickoff-view" data-mode={mode} className="space-y-4">
      {/* KS2-07 — header: mode toggle + answered counter + autosave status + the
          action buttons. Sticky, so it survives an outer-page scroll too: the
          complaint was that «complete meeting» could not be found, and a control
          that is only reachable by scrolling past the whole questionnaire is, for
          the person using it, a control that does not exist. */}
      <div
        data-testid="kickoff-toolbar"
        className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface px-1 py-2"
      >
        <div className="flex items-center gap-3">
          {/* The toggle ALWAYS shows the label/icon of the mode it switches TO
              (legacy: isNotesMode ? 'Questionnaire'/fa-list-check :
              'Meeting Notes'/fa-pen-to-square), rounded-full pill. */}
          <button
            type="button"
            data-testid="kickoff-mode-toggle"
            onClick={toggleMode}
            title="Switch view mode"
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-medium text-text hover:bg-surface-muted"
          >
            <i
              className={`fa-solid ${isNotesMode ? 'fa-list-check' : 'fa-pen-to-square'}`}
              aria-hidden
            />
            {isNotesMode ? 'Questionnaire' : 'Meeting Notes'}
          </button>
          <span data-testid="kickoff-answered-count" className="text-sm text-text-muted">
            {totals.answered}/{totals.total} answered
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span
            data-testid="kickoff-save-status"
            data-status={saveStatus}
            className="text-xs text-text-muted"
          >
            {saveStatus === 'saving'
              ? 'Saving…'
              : saveStatus === 'saved'
                ? 'Saved'
                : saveStatus === 'error'
                  ? 'Save failed'
                  : ''}
          </span>
          {renderSaveRow()}
        </div>
      </div>

      {/* Two-pane layout (legacy renderStage3Content Mode A / Mode B): BOTH
          panels are ALWAYS in the DOM — the mode only swaps which one is the wide
          "main" column and which is the "aside" cheat-sheet. E02-F05-T04 rework
          (PM visual acceptance): the SAME 60/40 grid as Pre-Kickoff
          (PreKickoffPanel `grid lg:grid-cols-5`, col-span-3 / col-span-2) — main =
          60%, aside = 40%, side by side from `lg` up, stacked below it. Each
          column scrolls independently inside the fixed-height row so the
          header/toggle stay put. Mode A (questions, default): questionnaire 60% /
          notes + data 40%. Mode B (notes): notes 60% / questionnaire cheat-sheet +
          data 40%. PK-QA-18: the yellow Missing-data block
          (`renderMissingBlock`) renders directly ABOVE the questionnaire in
          whichever slot the questionnaire occupies (main in Mode A, aside in Mode
          B) — exactly once, never duplicated. The Save/Complete-Meeting row lives in
          the sticky header ABOVE this block (KS2-07), not inside a scroller; the
          CompanyFactsPanel (E03-F02-T11) + KickoffDataPanel always sit at the
          bottom of the active `aside`, in that order. */}
      <div
        data-testid="kickoff-columns"
        className="grid gap-6 lg:h-[calc(100vh-300px)] lg:grid-cols-5 lg:grid-rows-[minmax(0,1fr)]"
      >
        <div
          data-testid="kickoff-main"
          className="flex min-w-0 flex-col gap-4 pr-1 lg:col-span-3 lg:min-h-0 lg:overflow-y-auto"
        >
          {!isNotesMode ? renderMissingBlock() : null}
          {isNotesMode ? renderNotes() : renderQuestionnaire()}
        </div>
        <aside
          data-testid="kickoff-aside"
          className="flex min-w-0 flex-col gap-4 pr-1 lg:col-span-2 lg:min-h-0 lg:overflow-y-auto"
        >
          {isNotesMode ? (
            <>
              {renderMissingBlock()}
              {renderQuestionnaire()}
            </>
          ) : (
            renderNotes()
          )}
          {companyFactsPanel}
          {dataPanel}
        </aside>
      </div>
    </div>
  );
}

export default KickoffView;
